// Metals.dev API integration for real-time precious metals prices
export interface KitcoPrice {
  symbol: string;
  name: string;
  price: number;
  change: number;
  changePercent: number;
  timestamp: string;
}

export interface MarketMetric {
  label: string;
  value: string;
  change: string;
  trend: 'up' | 'down';
  icon: string;
}

/**
 * Fetches real-time precious metals prices via Next.js API route
 * This avoids CORS issues by using server-side proxy to metals.dev
 */
export async function fetchKitcoPrices(): Promise<KitcoPrice[]> {
  try {
    const response = await fetch('/api/kitco-prices', {
      method: 'GET',
      cache: 'no-store',
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    const prices: KitcoPrice[] = await response.json();
    return prices;
  } catch (error) {
    console.error('Error fetching metals.dev prices:', error);
    // Return fallback data if API fails
    return getFallbackPrices();
  }
}

/**
 * Fallback prices if metals.dev API is unavailable
 */
function getFallbackPrices(): KitcoPrice[] {
  return [
    {
      symbol: 'XAU',
      name: 'Gold',
      price: 2048.50,
      change: 15.20,
      changePercent: 0.75,
      timestamp: new Date().toISOString(),
    },
    {
      symbol: 'XAG',
      name: 'Silver',
      price: 24.85,
      change: 0.35,
      changePercent: 1.43,
      timestamp: new Date().toISOString(),
    },
  ];
}

/**
 * Converts Kitco price data to market metrics format
 */
export function convertToMarketMetrics(prices: KitcoPrice[]): MarketMetric[] {
  const metrics: MarketMetric[] = [];
  
  prices.forEach((price) => {
    let label = '';
    let icon = '';
    
    switch (price.symbol) {
      case 'XAU':
        label = 'Gold Spot Price';
        icon = 'CurrencyDollarIcon';
        break;
      case 'XAG':
        label = 'Silver Spot Price';
        icon = 'SparklesIcon';
        break;
      case 'XPT':
        label = 'Platinum Spot Price';
        icon = 'CircleStackIcon';
        break;
      case 'XPD':
        label = 'Palladium Spot Price';
        icon = 'StarIcon';
        break;
      default:
        return;
    }
    
    metrics.push({
      label,
      value: `$${price.price.toFixed(2)}`,
      change: `${price.changePercent >= 0 ? '+' : ''}${price.changePercent.toFixed(2)}%`,
      trend: price.change >= 0 ? 'up' : 'down',
      icon,
    });
  });
  
  return metrics;
}

/**
 * Fetches historical price data for charting
 * Note: This is a placeholder - Kitco doesn't provide free historical data API
 * You would need to implement this with a different service or database
 */
export async function fetchHistoricalPrices(symbol: string, days: number = 30) {
  // This would need to be implemented with a proper data provider
  // For now, return mock data structure
  return generateMockHistoricalData(symbol, days);
}

function generateMockHistoricalData(symbol: string, days: number) {
  const data = [];
  const today = new Date();
  
  for (let i = days; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    
    data.push({
      date: date.toISOString().split('T')[0],
      price: Math.random() * 100 + 2000, // Mock price
      volume: Math.random() * 1000000,
    });
  }
  
  return data;
}